import Box from '@mui/material/Box';
import InputAdvancedSelect from 'common/components/Elements/Form/InputAdvancedSelection/InputAdvancedSelect';
import InputCheckbox from 'common/components/Elements/Form/InputCheckbox/InputCheckbox';
import InputRadioGroup from 'common/components/Elements/Form/InputRadioGroup/InputRadioGroup';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import SimpleModal from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import { buildFileName, downloadTextFile } from 'common/utilities/browser/downloadFile/downloadFile';
import { arrayUnique } from 'common/utilities/core/array';
import { useAppSelector } from 'core/redux/store/configureStore';
import type { ValueLabelOption } from 'core/utilities/core.types';
import { handleAsyncException } from 'core/utilities/exceptionHandler/exceptionHandler';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';
import type { EntitySequence as Sequence, SequenceAlign, SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { SequenceSelection } from 'sequenceAnalyser/utilities/selection/selection';
import {
  sequenceFeaturesSelectionAsCSV,
} from 'sequenceAnalyser/utilities/selectionActions/selectionActions';
import type { FeatureDefinition, FeatureDefinitions } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';

const featuresAsOptions = (features: string[]) => (
  features
    .sort((a, b) => a.localeCompare(b))
    .map((f) => ({ label: f, value: f }))
);

interface ComponentProps {
  open: boolean;
  onOpen: (open: boolean) => void;
  selection: SequenceSelection;
  rawSequences: Sequence[];
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  sequenceType: SequenceType;
  featureDefinitions: FeatureDefinitions;
}

function FeaturesToCSVFileExportModal({
  open, onOpen, rawSequences, sequences, alignedSequences, selection, sequenceType, featureDefinitions,
}: ComponentProps) {
  const fileFormat = useAppSelector((state) => (
    state.configuration.configuration.table_export_import_format as 'csv' | 'tsv'
  ));

  // Scope
  const scopes: ValueLabelOption[] = [
    { value: 'all', label: I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.all },
    {
      value: 'selection',
      label: I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.selection(
        selection.ids.length,
      ),
      disabled: selection.ids.length === 0,
    },
  ];

  const [scope, setScope] = React.useState(selection.ids.length ? 'selection' : 'all');

  // Format sequences
  const contents: ValueLabelOption[] = [
    { value: 'raw', label: I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.raw },
    { value: 'aligned', label: I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.aligned },
  ];

  const [content, setContent] = React.useState('aligned');

  // Full sequence
  const [includeFullSequence, setIncludeFullSequence] = useState(true);

  // Feature types: CDR and Framework, if none default to visible features
  const features = arrayUnique(
    (Object.values(featureDefinitions) as FeatureDefinition[])
      .map((fd) => fd.type),
  );
  const CDRAndFrameworkFeatures = arrayUnique(
    (Object.values(featureDefinitions) as FeatureDefinition[])
      .filter((fd) => fd.visible)
      .filter((fd) => fd.type.match(/^(CDR|Framework)$/) !== null)
      .map((fd) => fd.type),
  );
  const visibleFeatures = arrayUnique(
    (Object.values(featureDefinitions) as FeatureDefinition[])
      .filter((fd) => fd.visible)
      .map((fd) => fd.type),
  );
  let defaultFeatures = CDRAndFrameworkFeatures;
  if (defaultFeatures.length === 0) defaultFeatures = visibleFeatures;
  const [selectedFeatures, setSelectedFeatures] = React.useState(defaultFeatures);

  // Download process
  const [loading, setLoading] = useState(false);

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.title(fileFormat)}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      content={(
        <Box mt={1}>
          <InputRadioGroup
            label={I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.scope}
            options={scopes}
            onChange={(value) => setScope(value)}
            value={scope}
            horizontal
            modalLabel
          />

          <InputRadioGroup
            label={I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.content}
            options={contents}
            onChange={(value) => {
              setContent(value);
            }}
            value={content}
            horizontal
            modalLabel
          />

          <InputCheckbox
            label={I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.includeFullSequence}
            checked={includeFullSequence}
            onChange={() => setIncludeFullSequence((prevState) => !prevState)}
          />

          <InputAdvancedSelect
            isMulti
            label={I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.features}
            ariaLabel={I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.features}
            options={featuresAsOptions(features)}
            values={featuresAsOptions(selectedFeatures)}
            onChange={(newValues) => {
              setSelectedFeatures((newValues || []).map((v) => v.value));
            }}
            modalLabel
          />

          <ModalActions
            applyText={I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.apply}
            persisting={loading}
            disableApply={selectedFeatures.length === 0}
            onApply={() => {
              setLoading(true);

              try {
                const fileContent = sequenceFeaturesSelectionAsCSV(
                  fileFormat,
                  content === 'aligned' ? 'aligned' : 'raw',
                  selectedFeatures,
                  rawSequences,
                  sequences,
                  alignedSequences,
                  scope === 'all' ? { ids: [] } : selection,
                  featureDefinitions,
                  includeFullSequence,
                );

                const filename = buildFileName([
                  sequenceType === 'protein' ? 'ProteinFeatureSequences' : 'NucleotideFeatureSequences',
                ].join('_'), fileFormat);

                downloadTextFile(filename, fileContent);

                setLoading(false);
                onOpen(false);
              } catch (e) {
                handleAsyncException(
                  e,
                  I18n.SequenceAnalyser.FeaturesToCSVFileExportModal.error,
                );
                onOpen(false);
              }
            }}
          />
        </Box>
      )}
    />
  );
}

export default FeaturesToCSVFileExportModal;
